import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../axiosConfig';
import { FiCalendar, FiSearch } from 'react-icons/fi';
import { CustomDatePicker } from '../commons/datepicker';
import '../../styles/AssignReportModal.css';

const AssignReportModal = ({ isOpen, onClose, dogId, templates, onAssigned, dogs = [] }) => {
    const { t } = useTranslation('reports');
    const [formData, setFormData] = useState({
        reportTemplateId: '',
        dogId: dogId || '',
        startDate: '',
        endDate: '',
        recurrence: 'ONCE'
    });
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredDogs, setFilteredDogs] = useState(dogs);

    // Filter dogs when search term changes
    useEffect(() => {
        if (!searchTerm.trim()) {
            setFilteredDogs(dogs);
            return;
        }

        const searchTermLower = searchTerm.toLowerCase();
        const filtered = dogs.filter(dog =>
            dog.name.toLowerCase().includes(searchTermLower) ||
            (dog.breed && dog.breed.toLowerCase().includes(searchTermLower)) ||
            (dog.ownersName && dog.ownersName.toLowerCase().includes(searchTermLower))
        );

        setFilteredDogs(filtered);
    }, [searchTerm, dogs]);

    // Recurrence options
    const recurrenceOptions = [
        { value: 'ONCE', label: t('recurrence.once') },
        { value: 'DAILY', label: t('recurrence.daily') },
        { value: 'EVERY_2_DAYS', label: t('recurrence.every2Days') },
        { value: 'EVERY_3_DAYS', label: t('recurrence.every3Days') },
        { value: 'EVERY_4_DAYS', label: t('recurrence.every4Days') },
        { value: 'EVERY_5_DAYS', label: t('recurrence.every5Days') },
        { value: 'EVERY_6_DAYS', label: t('recurrence.every6Days') },
        { value: 'WEEKLY', label: t('recurrence.weekly') }
    ];

    // Reset form
    const resetForm = () => {
        setFormData({
            reportTemplateId: '',
            dogId: dogId || '',
            startDate: '',
            endDate: '',
            recurrence: 'ONCE'
        });
        setSearchTerm('');
    };

    // Handle form submission
    const handleSubmit = async () => {
        if (!formData.reportTemplateId || !formData.startDate || !formData.dogId) {
            return;
        }

        try {
            setLoading(true);
            const token = localStorage.getItem('jwt');
            const response = await axios.post('/api/v1/reports/assignments', formData, {
                headers: { Authorization: `Bearer ${token}` }
            });

            onAssigned(response.data);
            onClose();
            resetForm();


        } catch (err) {
            console.error('Error assigning report:', err);
            const errorMessage = err.response?.data?.message || t('assignError');
        } finally {
            setLoading(false);
        }
    };

    // Get current date in YYYY-MM-DD format for date input
    const getTodayDate = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    if (!isOpen) return null;

    return (
        <div className="assign-modal-overlay" onClick={onClose}>
            <div className="assign-modal-content" onClick={e => e.stopPropagation()}>
                <div className="assign-modal-header">
                    <h2>{t('assignReport')}</h2>
                    <button
                        className="assign-modal-close"
                        onClick={onClose}
                    >
                        &times;
                    </button>
                </div>

                <div className="assign-modal-body">
                    <div className="assign-form-group">
                        <label htmlFor="reportTemplate">{t('selectTemplate')}</label>
                        <select
                            id="reportTemplate"
                            className="assign-form-select"
                            value={formData.reportTemplateId}
                            onChange={(e) => setFormData({ ...formData, reportTemplateId: e.target.value })}
                        >
                            <option value="">{t('selectTemplatePlaceholder')}</option>
                            {templates.map(template => (
                                <option key={template.id} value={template.id}>
                                    {template.title}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Show dog selection dropdown only if no dogId was provided */}
                    {!dogId && (
                        <div className="assign-form-group">
                            <label htmlFor="dogSelect">{t('dog')}</label>

                            {/* Search bar for filtering dogs */}
                            <div className="assign-search-container">
                                <div className="assign-search-wrapper">
                                    <FiSearch className="assign-search-icon" />
                                    <input
                                        type="text"
                                        className="assign-search-input"
                                        placeholder={t('searchDogPlaceholder')}
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>

                            <select
                                id="dogSelect"
                                className="assign-form-select"
                                value={formData.dogId}
                                onChange={(e) => setFormData({ ...formData, dogId: e.target.value })}
                                size={filteredDogs.length > 5 ? 5 : Math.max(2, filteredDogs.length)}
                            >
                                {filteredDogs.map((dog) => (
                                    <option key={dog.id} value={dog.id}>
                                        {dog.name} ({dog.breed}) - {dog.ownersName}
                                    </option>
                                ))}
                            </select>

                            {filteredDogs.length === 0 && searchTerm && (
                                <div className="assign-no-results">
                                    {t('noDogsFound', { search: searchTerm })}
                                </div>
                            )}
                        </div>
                    )}

                    <div className="assign-form-group">
                        <label htmlFor="startDate">{t('startDate')}</label>
                        <CustomDatePicker
                            id="startDate"
                            name="startDate"
                            min={getTodayDate()}
                            value={formData.startDate}
                            onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                        />
                    </div>

                    <div className="assign-form-group">
                        <label htmlFor="endDate">{t('endDateOptional')}</label>
                        <CustomDatePicker
                            id="endDate"
                            name="endDate"
                            min={getTodayDate()}
                            value={formData.endDate}
                            onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                        />
                    </div>

                    <div className="assign-form-group">
                        <label htmlFor="recurrence">{t('recurrenceLabel')}</label>
                        <select
                            id="recurrence"
                            className="assign-form-select"
                            value={formData.recurrence}
                            onChange={(e) => setFormData({ ...formData, recurrence: e.target.value })}
                        >
                            {recurrenceOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="assign-modal-footer">
                    <button
                        className="assign-cancel-btn"
                        onClick={onClose}
                        disabled={loading}
                    >
                        {t('cancel')}
                    </button>
                    <button
                        className="assign-submit-btn"
                        onClick={handleSubmit}
                        disabled={loading || !formData.reportTemplateId || !formData.startDate || !formData.dogId}
                    >
                        {loading ? t('assigning') : t('assign')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AssignReportModal;